// ============================================================
// useCanViewCaseHistory.ts
// Computes whether the CURRENT user may see the CURRENT case's History
// section, per the firm's caseHistoryVisibility setting. This is UX
// SUPPRESSION ONLY — the backend audit-log endpoint is the real security
// boundary (it still 403s). The hook exists so the History section AND
// its toggle entry can be hidden together (no 403 error banner for
// excluded viewers), with one shared computation.
//
// Rule (per THIS case's assignees):
//   isAdmin
//   || setting === 'firm'
//   || (setting === 'assignee' && I'm ON the case — the owner / lead fee
//       earner OR any handling-team member / co-assignee (utils/caseTeam))
//   (setting === 'admin' → admins only)
//
// While the setting is still loading (null) or there's no current case,
// non-admins get FALSE — fail-safe: hide rather than flash a section that
// then errors. Admins always get TRUE regardless of the setting.
//
// isAdmin is read from authUser.role (the AUTHORITATIVE role from the
// auth/me payload — same field Sidebar/AdminPage gate on), NOT the store's
// `userRole` field. `userRole` is a manual demo role-switcher that
// defaults to 'user' and is never synced from auth, so reading it here
// wrongly gated real admins whenever they hadn't flipped that toggle.
// ============================================================
import { useCasevoStore } from '../store/useCasevoStore'
import { isOnCaseTeam } from '../utils/caseTeam'

export function useCanViewCaseHistory(): boolean {
  const authUser = useCasevoStore((s) => s.authUser)
  const currentCase = useCasevoStore((s) => s.currentCase)
  const visibility = useCasevoStore((s) => s.caseHistoryVisibility)

  // Authoritative admin flag — from the JWT-backed auth payload.
  const isAdmin = authUser?.role === 'admin'
  if (isAdmin) return true
  if (visibility === 'firm') return true
  if (visibility === 'assignee') {
    return isOnCaseTeam(authUser?.id, currentCase)
  }
  // 'admin', null (not loaded), or any unexpected value → non-admins hidden.
  return false
}
